import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import ServiceSearchBar from "./ServiceSearchBar";
import Spinner from "../../utils/Spinner";
import { Store } from "lucide-react";
import { getImage } from "../../utils/cloudinarySizing";

export const ServicesUI = ({
  providerType,
  selectedCategory,
  searchQuery,
  providers,
  loading,
  firstLoadComplete,
  totalPages,
  setTypeFilter,
  setSearch,
  goToPage,
  handleToggleFavorite
}) => {
  const navigate = useNavigate();
  const [showDropup, setShowDropup] = useState(false);

  const location = useLocation();

  const categories = [
    { id: 1, name: "General Worker", image: "/shovel.svg" },
    { id: 2, name: "Handyman", image: "/drill.svg" },
    { id: 3, name: "Painting", image: "/paint-brush.svg" },
    { id: 4, name: "Cleaning", image: "/broom.svg" },
    { id: 5, name: "Carpentry", image: "/hammer.svg" },
    { id: 6, name: "Electrical", image: "/plug.svg" },
    { id: 7, name: "Plumbing", image: "/plunger.svg" },
    { id: 8, name: "Moving / Cleanouts", image: "/box.svg" },
  ];

  const visibleCategories =
    providerType === "business"
      ? categories.filter(
        c => !["General Worker", "Handyman"].includes(c.name)
      )
      : categories;

  const handleCategoryClick = (category) => {
    const encoded = encodeURIComponent(category);

    if (location.pathname.startsWith("/freelancers")) {
      navigate(`/freelancers?category=${encoded}`);
    }

    else if (location.pathname.startsWith("/businesses")) {
      navigate(`/businesses?category=${encoded}`);
    }

    else {
      // fallback (optional)
      navigate(`/freelancers?category=${encoded}`);
    }
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;
    const pages = [];
    for (let i = 1; i <= totalPages; i++) {
      pages.push(
        <button
          key={i}
          className={`pagination-btn ${i === parseInt(new URLSearchParams(window.location.search).get("page") || "1") ? "active" : ""}`}
          onClick={() => goToPage(i)}
        >
          {i}
        </button>
      );
    }
    return <div className="pagination-container">{pages}</div>;
  };

  if (!firstLoadComplete || loading) {
    return <Spinner />; // show spinner until first load finishes
  }

  const formatLocation = (city, state, country = "US") => {
    if (!city && !state) return null;

    const parts = [];
    if (city) parts.push(city);
    if (state) parts.push(state);

    if (country && country !== "US") {
      parts.push(country);
    }

    return parts.join(", ");
  };

  const renderProviderCard = (p) => {
    const displayService = p.category?.name || "No service listed";

    const placeholderImage =
      p.type === "freelancer"
        ? "/servspace-placeholder-500x500.png"
        : "/servspace-business-placeholder.png";

    const imageClass =
      p.type === "freelancer"
        ? "category-image freelancer-image"
        : "category-image business-image";

    const isBusiness = p.type === "business";

    const containerClass = isBusiness
      ? "business-image-container no-shadow"
      : "main-image-container";



    return (
      <div
        key={`${p.type}-${p.id}`}
        className="category-box"
        onClick={() => navigate(`/${p.type}/${p.id}`)}
      >
        <div className={containerClass}>
          <img
            src={getImage(
              p.profile?.photo_original,
              p.type === "business" ? "srv_med" : "avatar_sm"
            ) || placeholderImage}
            className={imageClass}
            alt={p.profile?.username || "Profile"}
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = placeholderImage;
            }}
          />
        </div>

        <div className="category-content">
          <div className="category-title">
            <span className="display-name">
              {p.type === "freelancer"
                ? `${p.profile?.first_name || ""} ${p.profile?.last_name || ""}`
                : p.business_name || ""}
            </span>

            <span className="user-name">
              <span className="pretty-at">@</span>
              {p.username || ""}
            </span>
          </div>

          <div className="category-service">

            <i className="fa-solid fa-wrench"></i> {displayService}
          </div>

          {(p.profile?.city || p.profile?.state) && (
            <div className="category-location">
              <i className="fa-solid fa-location-dot" />{" "}
              {formatLocation(p.profile?.city, p.profile?.state)}
            </div>
          )}

          <div className="category-bottom">
            <div
              className="category-subscribers"
              onClick={(e) => handleToggleFavorite(e, p.id, p.type)}
            >
              <button className={`save-button ${p.is_favorited ? "favorited" : "unfavorited"}`}>
                <i className="fa-solid fa-heart" />
              </button>
              <span>{p.favorited_count || 0}</span>
            </div>

            <div
              className="category-rating"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/${p.type}/${p.id}/reviews`);
              }}
            >
              <i className="fa-solid fa-star" />
              <span>
                {p.average_rating > 0
                  ? Number.isInteger(p.average_rating)
                    ? p.average_rating
                    : p.average_rating.toFixed(1)
                  : "0"}
                {p.review_count > 0 && ` (${p.review_count})`}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div id="content-scroll">
      <div className="main-home">
        <div className="results-scroll">
          {/* Categories */}
          <div className="category-section">
            <div className="category-menu-wrapper">
              {visibleCategories.map((c) => (
                <div
                  key={c.id}
                  className={`category-item ${selectedCategory === c.name ? "active" : ""}`}
                  onClick={() => handleCategoryClick(c.name)}
                >
                  <img src={c.image} alt={c.name} className="category-icon" />
                  <br />
                  <span className="category-name">{c.name}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="section-container">

            {providers.length === 0 ? (
              <div className="empty-placeholder">
                <p className="empty-text">No results found.</p>
              </div>
            ) : (
              <div className="categories-grid">
                {providers.map(renderProviderCard)}
              </div>
            )}

            {renderPagination()}
          </div>

        </div>

        {/* Search + Filter */}
        <div className="search-bar-wrapper">
          <div className="search-side left">{/* blank for symmetry */}</div>

          <div className="search-center">
            <ServiceSearchBar onSearch={setSearch} />
          </div>

          <div className="search-side right">
            <div className="filter-dropup-container">

              {showDropup && (
                <div className="filter-dropup-menu">
                  <button
                    className={providerType === "all" ? "active" : ""}
                    onClick={() => {
                      setTypeFilter("all");
                      setShowDropup(false);
                    }}
                  >
                    <i className="fa-solid fa-globe"></i> See All
                  </button>

                  <button
                    className={providerType === "freelancers" ? "active" : ""}
                    onClick={() => {
                      setTypeFilter("freelancers");
                      setShowDropup(false);
                    }}
                  >
                    <i className="fa-solid fa-user"></i> Freelancers
                  </button>

                  <button
                    className={providerType === "businesses" ? "active" : ""}
                    onClick={() => {
                      setTypeFilter("businesses");
                      setShowDropup(false);
                    }}
                  >
                    <i className="fa-solid fa-building"></i> Businesses
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};